import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chatable/ui-components";
import { authApi, getAuthErrorMessage } from "../api/auth";
import { AUTH_MIGRATION_RETRY_REQUIRED } from "@chatable/chat-client/federated-auth";
import { setAuthToken, setRefreshFunction, setGlobalErrorHandler } from "../api/axios";
import { authRefreshManager } from "../api/authRefreshManager";
import { trpcClient, setTrpcErrorHandler } from "../api/trpc";
import { queryKeys } from "../api/queryClient";
import { generateDeviceFingerprint } from "../utils/deviceFingerprint";
import { analyticsService } from "../services/analytics/analyticsService";
import { createLogger } from "../utils/logger";
import { sanitizeRedirectPath } from "../utils/navigation";
import type { User } from "../api/axios";
import type {
  SignInRequest,
  SignUpRequest,
  ConfirmSignUpRequest,
  ResendVerificationCodeRequest,
  OAuthProvider,
} from "../api/auth";

// Forgot password requests
export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
}

// Auth state interface
export interface AuthState {
  user: User | null;
  accessToken: string | null;
  status: "idle" | "loading" | "authenticated" | "error";
  isLoading: boolean;
  isAuthenticated: boolean;
  hasInitialized: boolean;
  isBootstrapping: boolean;
  error: string | null;
  errorCode: string | null;
}

// Extended ConfirmSignUpRequest to support auto sign-in
export interface ExtendedConfirmSignUpRequest extends ConfirmSignUpRequest {
  password?: string;
}

// Auth context interface
export interface AuthContextType extends AuthState {
  signIn: (data: SignInRequest) => Promise<void>;
  signUp: (data: SignUpRequest) => Promise<void>;
  confirmSignUp: (data: ExtendedConfirmSignUpRequest) => Promise<void>;
  resendVerificationCode: (data: ResendVerificationCodeRequest) => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  // OAuth methods
  signInWithGoogle: (options?: { redirectTo?: string }) => Promise<void>;
  signInWithApple: () => Promise<void>;
  handleOAuthCallback: (code: string, state: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create logger for auth context
const logger = createLogger("AuthContext");

// Safe localStorage access for auth tokens
const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: "chatable_access_token",
  REFRESH_TOKEN: "chatable_refresh_token",
  COGNITO_REFRESH_TOKEN: "chatable_cognito_refresh_token",
  AUTH_EMAIL: "authEmail",
  USER: "chatable_user",
} as const;

const getStoredToken = (key: string): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStoredToken = (key: string, value: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Silently fail if localStorage is not available
  }
};

const removeStoredToken = (key: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Silently fail if localStorage is not available
  }
};

const getStoredUser = (): User | null => {
  const raw = getStoredToken(AUTH_STORAGE_KEYS.USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
};

const setStoredUser = (user: User | null): void => {
  if (!user) {
    removeStoredToken(AUTH_STORAGE_KEYS.USER);
    return;
  }
  setStoredToken(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
};

// Safe localStorage access for email
const setStoredEmail = (email: string): void => {
  setStoredToken(AUTH_STORAGE_KEYS.AUTH_EMAIL, email);
};

const removeStoredEmail = (): void => {
  removeStoredToken(AUTH_STORAGE_KEYS.AUTH_EMAIL);
};

/**
 * Guards the migration retry to one per sign-in attempt: set when the retry is
 * issued, cleared when the attempt ends either way, so a server that kept
 * asking could not bounce us around the redirect.
 */
const MIGRATION_RETRY_KEY = "oauth_migration_retry";

const parseJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (base64.length % 4)) % 4;
  const padded = `${base64}${"=".repeat(padLength)}`;
  try {
    if (typeof globalThis.atob !== "function") return null;
    const decoded = globalThis.atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const getJwtStringClaim = (payload: Record<string, unknown>, key: string): string | undefined => {
  const value = payload[key];
  return typeof value === "string" ? value : undefined;
};

const getJwtNumberClaim = (payload: Record<string, unknown>, key: string): number | undefined => {
  const value = payload[key];
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const isJwtExpired = (token: string, skewSeconds = 30): boolean => {
  const payload = parseJwtPayload(token);
  if (!payload) return true;
  const exp = getJwtNumberClaim(payload, "exp");
  if (!exp) return false;
  const expiresAt = exp * 1000;
  return Date.now() + skewSeconds * 1000 >= expiresAt;
};

const buildUserFromJwt = (token: string): User | null => {
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  if (isJwtExpired(token)) return null;

  const email = getJwtStringClaim(payload, "email");
  const cognitoUsername = getJwtStringClaim(payload, "cognito:username");
  const name =
    getJwtStringClaim(payload, "name") ||
    [getJwtStringClaim(payload, "given_name"), getJwtStringClaim(payload, "family_name")]
      .filter(Boolean)
      .join(" ")
      .trim();
  const username = getJwtStringClaim(payload, "username") || cognitoUsername || name;
  const sub = getJwtStringClaim(payload, "sub") || username || email;

  if (!sub) return null;

  const derivedEmail = email || (username && username.includes("@") ? username : "");
  const emailVerified = Boolean(payload.email_verified) || Boolean(payload.emailVerified);

  return {
    sub,
    email: derivedEmail ?? "",
    username: username || derivedEmail || "Unknown User",
    emailVerified,
  };
};

const getOptimisticUser = (accessToken: string | null): User | null => {
  if (!accessToken) return null;
  const tokenUser = buildUserFromJwt(accessToken);
  if (!tokenUser) return null;
  const cachedUser = getStoredUser();
  if (cachedUser && cachedUser.sub === tokenUser.sub) {
    return cachedUser;
  }
  return tokenUser;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Initialize tokens from localStorage
  const [accessToken, setAccessTokenState] = useState<string | null>(() => {
    const stored = getStoredToken(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    if (stored) {
      setAuthToken(stored); // Set it in axios immediately
    }
    return stored;
  });

  const [refreshToken, setRefreshTokenState] = useState<string | null>(() => {
    return getStoredToken(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  });

  const [cognitoRefreshToken, setCognitoRefreshTokenState] = useState<string | null>(() => {
    return getStoredToken(AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN);
  });

  const [optimisticUser, setOptimisticUser] = useState<User | null>(() =>
    getOptimisticUser(accessToken),
  );
  const [status, setStatus] = useState<AuthState["status"]>(() => {
    if (optimisticUser) return "authenticated";
    return accessToken || refreshToken || cognitoRefreshToken ? "loading" : "idle";
  });
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!optimisticUser);
  const [hasInitialized, setHasInitialized] = useState(false);
  const hasSessionTokens = Boolean(accessToken || refreshToken || cognitoRefreshToken);
  const isBootstrapping = hasSessionTokens && !hasInitialized && !optimisticUser;

  // Create a ref for signOut to avoid circular dependency in mutations
  const signOutRef = useRef<() => Promise<void>>();

  // Set up global error handler for axios and tRPC
  useEffect(() => {
    const showGlobalError = (message: string) => {
      addToast({
        type: "error",
        message,
        duration: 6000,
      });
    };

    setGlobalErrorHandler(showGlobalError);
    setTrpcErrorHandler(showGlobalError);

    return () => {
      setGlobalErrorHandler(null);
      setTrpcErrorHandler(null);
    };
  }, [addToast]);

  // Helper functions to manage token persistence
  const updateAccessToken = useCallback((token: string | null) => {
    setAccessTokenState(token);
    setAuthToken(token);
    if (token) {
      setStoredToken(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
    } else {
      removeStoredToken(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    }
  }, []);

  const updateRefreshToken = useCallback((token: string | null) => {
    setRefreshTokenState(token);
    if (token) {
      setStoredToken(AUTH_STORAGE_KEYS.REFRESH_TOKEN, token);
    } else {
      removeStoredToken(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    }
  }, []);

  const updateCognitoRefreshToken = useCallback((token: string | null) => {
    setCognitoRefreshTokenState(token);
    if (token) {
      setStoredToken(AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN, token);
    } else {
      removeStoredToken(AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN);
    }
  }, []);

  // Create signOut function early to avoid circular dependency
  const signOut = useCallback(async () => {
    // Track sign out event before clearing state
    analyticsService.track("user.signed_out", {});

    // Revoke this device's session server-side; local state clears either way.
    if (refreshToken && cognitoRefreshToken) {
      try {
        await authApi.logout({ refreshToken, cognitoRefreshToken });
      } catch {
        // Offline or already revoked: nothing left to do server-side.
      }
    }

    // Stop private observers before the unauthenticated navigation tree can
    // render. Clearing after the state flip allowed mounted queries to race one
    // last unauthorized request.
    await queryClient.cancelQueries();
    queryClient.clear();

    updateAccessToken(null);
    updateRefreshToken(null);
    updateCognitoRefreshToken(null);
    setOptimisticUser(null);
    setStoredUser(null);
    setStatus("idle");
    setIsAuthenticated(false);
    setError(null);
    setErrorCode(null);

    // Clear stored email
    removeStoredEmail();

    // Reset analytics identification and session
    analyticsService.reset();
  }, [
    refreshToken,
    cognitoRefreshToken,
    queryClient,
    updateAccessToken,
    updateRefreshToken,
    updateCognitoRefreshToken,
  ]);

  // Assign signOut to ref so mutations can use it
  signOutRef.current = signOut;

  // Get current user query with conservative refetch settings
  const {
    data: userData,
    isLoading: userLoading,
    error: userError,
  } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: authApi.me,
    enabled: !!accessToken && hasInitialized,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  useEffect(() => {
    if (userData?.data) {
      setOptimisticUser(userData.data);
      setStoredUser(userData.data);
    }
  }, [userData?.data]);

  // Sign in mutation
  const signInMutation = useMutation({
    mutationFn: authApi.signIn,
    onSuccess: response => {
      const {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        cognitoRefreshToken: newCognitoRefreshToken,
        user,
      } = response.data;

      updateAccessToken(newAccessToken);
      updateRefreshToken(newRefreshToken || null);
      updateCognitoRefreshToken(newCognitoRefreshToken || null);
      setStatus("authenticated");
      setIsAuthenticated(true);
      setError(null);

      // Set user data immediately from sign in response to avoid delay
      queryClient.setQueryData(queryKeys.auth.me, {
        success: true,
        data: user,
      });
      setOptimisticUser(user);
      setStoredUser(user);

      // Store email for refresh token usage
      setStoredEmail(user.email);

      // Track sign in and identify user via email
      analyticsService.identify(user.email, {
        email: user.email,
        username: user.username,
      });
      analyticsService.track("user.signed_in", { method: "email" });
    },
    onError: (error: unknown) => {
      const err = error as { data?: { cause?: string }; message?: string };
      let errorCode = null;
      const errorMessage = err?.data?.cause
        ? getAuthErrorMessage(err.data.cause as string)
        : err?.message || "Sign in failed. Please try again.";

      // Check if it's a "User not confirmed" error
      if (
        err?.data?.cause === "AUTH007" ||
        errorMessage.includes("User account is not confirmed")
      ) {
        errorCode = "AUTH007";
      }

      setError(errorMessage);
      setErrorCode(errorCode);
      setStatus("error");
    },
  });

  // Sign up mutation
  const signUpMutation = useMutation({
    mutationFn: async (data: SignUpRequest) => {
      const result = await trpcClient.auth.signUp.mutate(data);
      return result;
    },
    onSuccess: () => {
      setError(null);
      setErrorCode(null);
      // Don't set authenticated status - user needs to confirm email first

      // Track sign ups, even if not yet confirmed
      analyticsService.track("user.signed_up", { method: "email" });
    },
    onError: (error: unknown) => {
      const err = error as { data?: { cause?: string }; message?: string };
      let errorCode = null;
      let errorMessage = err?.message || "Sign up failed. Please try again.";

      // Check if it's an "Email already registered" error
      if (
        errorMessage.includes("Email is already registered") ||
        errorMessage.includes("Email already registered")
      ) {
        errorCode = "AUTH002";
      }

      setError(errorMessage);
      setErrorCode(errorCode);
      setStatus("error");
    },
  });

  // Confirm sign up mutation
  const confirmSignUpMutation = useMutation({
    mutationFn: async (data: ConfirmSignUpRequest & { password?: string }) => {
      await authApi.confirmSignUp({ email: data.email, code: data.code });
      return data;
    },
    onSuccess: async data => {
      setError(null);

      // If we have the password (from sign-in flow), automatically sign in
      if (data.password) {
        try {
          const deviceFingerprint = await generateDeviceFingerprint();
          await signInMutation.mutateAsync({
            email: data.email,
            password: data.password,
            deviceFingerprint,
          });
        } catch {
          // If auto sign-in fails, user will need to sign in manually
          // The error will be handled by the sign-in mutation
        }
      }
    },
    onError: (error: unknown) => {
      const err = error as { data?: { cause?: string }; message?: string };
      const errorMessage = err?.data?.cause
        ? getAuthErrorMessage(err.data.cause as string)
        : err?.message || "Email confirmation failed. Please try again.";
      setError(errorMessage);
      setStatus("error");
    },
  });

  // Resend verification code mutation
  const resendVerificationCodeMutation = useMutation({
    mutationFn: authApi.resendVerificationCode,
    onSuccess: () => {
      setError(null);
      addToast({
        type: "success",
        message: "Verification code sent to your email",
        duration: 4000,
      });
    },
    onError: (error: unknown) => {
      const err = error as { data?: { cause?: string }; message?: string };
      const errorMessage = err?.data?.cause
        ? getAuthErrorMessage(err.data.cause as string)
        : err?.message || "Failed to resend verification code. Please try again.";
      setError(errorMessage);
      setStatus("error");
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: async (data: ForgotPasswordRequest) => {
      const result = await trpcClient.auth.forgotPassword.mutate(data);
      return result;
    },
    onSuccess: () => {
      setError(null);
      addToast({
        type: "success",
        message: "Password reset code sent to your email",
        duration: 4000,
      });
      analyticsService.track("user.password_reset_requested", {});
    },
    onError: (error: unknown) => {
      const err = error as { data?: { cause?: string }; message?: string };
      const errorMessage = err?.data?.cause
        ? getAuthErrorMessage(err.data.cause as string)
        : err?.message || "Failed to send reset code. Please try again.";
      setError(errorMessage);
      setStatus("error");
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordRequest) => {
      const result = await trpcClient.auth.resetPassword.mutate(data);
      return result;
    },
    onSuccess: () => {
      setError(null);
      addToast({
        type: "success",
        message: "Password reset successfully!",
        duration: 4000,
      });
      analyticsService.track("user.password_reset_completed", {});
    },
    onError: (error: unknown) => {
      const err = error as { data?: { cause?: string }; message?: string };
      const errorMessage = err?.data?.cause
        ? getAuthErrorMessage(err.data.cause as string)
        : err?.message || "Failed to reset password. Please try again.";
      setError(errorMessage);
      setStatus("error");
    },
  });

  // Create stable refresh function using global refresh manager
  const performRefresh = useCallback(async () => {
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }
    if (!cognitoRefreshToken) {
      throw new Error("No Cognito refresh token available");
    }
    // Generate device fingerprint for security validation
    const deviceFingerprint = await generateDeviceFingerprint();
    // Use global refresh manager to prevent race conditions
    return await authRefreshManager.refresh(refreshToken, cognitoRefreshToken, deviceFingerprint);
  }, [refreshToken, cognitoRefreshToken]);

  // Refresh token mutation
  const refreshMutation = useMutation({
    mutationFn: performRefresh,
    onSuccess: async response => {
      // Response is now directly the RefreshResult from authRefreshManager
      const { accessToken: newAccessToken, refreshToken: rotatedRefreshToken } = response;

      // Update access token
      updateAccessToken(newAccessToken);

      // Refresh endpoint rotates our refresh token; persist the new one
      if (rotatedRefreshToken) {
        updateRefreshToken(rotatedRefreshToken);
      }

      setError(null);
      setErrorCode(null);

      // Wait a tick to ensure token is set in axios interceptor
      await new Promise(resolve => window.setTimeout(resolve, 0));

      // After refresh success, manually fetch user data
      try {
        const userResponse = await authApi.me();
        queryClient.setQueryData(queryKeys.auth.me, userResponse);
        setOptimisticUser(userResponse.data);
        setStoredUser(userResponse.data);
        setStatus("authenticated");
        setIsAuthenticated(true);
      } catch (error) {
        // Don't automatically sign out on first failure - could be network issue
        // Only sign out if it's actually an auth error
        const err = error as { response?: { status?: number } };
        if (err.response?.status === 401) {
          signOutRef.current?.();
        }
      }
    },
    onError: error => {
      // If we still have an access token, keep using it
      if (accessToken) {
        // Access token might still be valid, don't log out
        setStatus("authenticated");
        setIsAuthenticated(true);
        logger.warn("Token refresh failed but keeping session with existing token", {
          error,
        });
        return;
      }

      // Only logout if we have no access token
      signOutRef.current?.();
    },
  });
  const refreshSession = refreshMutation.mutateAsync;

  useEffect(() => {
    const initAuth = async () => {
      // Skip if already initialized or currently refreshing
      if (hasInitialized || refreshMutation.isPending) {
        return;
      }

      if (!optimisticUser) {
        setStatus("loading");
      }

      if (accessToken) {
        try {
          // Validate the access token by calling auth.me
          const userResponse = await authApi.me();

          // Token is valid - set user data and mark as authenticated
          queryClient.setQueryData(queryKeys.auth.me, userResponse);
          setOptimisticUser(userResponse.data);
          setStoredUser(userResponse.data);
          setStatus("authenticated");
          setIsAuthenticated(true);
          setHasInitialized(true);
          return;
        } catch {
          // Access token is invalid/expired, try to refresh if we have refresh token
          if (refreshToken) {
            try {
              await refreshMutation.mutateAsync();
              // Refresh successful, status will be set by refresh mutation
              setHasInitialized(true);
              return;
            } catch {
              // Both access token and refresh failed - clear tokens and sign out
              updateAccessToken(null);
              updateRefreshToken(null);
              setOptimisticUser(null);
              setStoredUser(null);
            }
          } else {
            // No refresh token available - clear invalid access token
            updateAccessToken(null);
            setOptimisticUser(null);
            setStoredUser(null);
          }
        }
      } else if (refreshToken) {
        // No access token but we have refresh token - try to refresh
        try {
          await refreshMutation.mutateAsync();
          // Refresh successful, status will be set by refresh mutation
          setHasInitialized(true);
          return;
        } catch {
          // Refresh failed - clear invalid refresh token
          updateRefreshToken(null);
          setOptimisticUser(null);
          setStoredUser(null);
        }
      }

      // No valid tokens found or all attempts failed
      setStatus("idle");
      setIsAuthenticated(false);
      setOptimisticUser(null);
      setStoredUser(null);
      setHasInitialized(true);
    };

    initAuth();
  }, [
    hasInitialized,
    refreshMutation,
    updateAccessToken,
    updateRefreshToken,
    accessToken,
    refreshToken,
    optimisticUser,
    queryClient,
  ]);

  // Set up automatic refresh function
  useEffect(() => {
    const refreshAuthWrapper = async () => {
      await refreshSession();
    };

    setRefreshFunction(refreshAuthWrapper);

    return () => {
      setRefreshFunction(null);
    };
  }, [refreshSession]);

  // Update status based on loading states only (don't override auth state)
  useEffect(() => {
    if (userLoading || signInMutation.isPending || refreshMutation.isPending) {
      if (!isAuthenticated) {
        setStatus("loading");
      }
    } else if (userError && !userData && !accessToken) {
      setStatus("error");
    }
  }, [
    userLoading,
    userError,
    userData,
    accessToken,
    isAuthenticated,
    signInMutation.isPending,
    refreshMutation.isPending,
  ]);

  const signIn = useCallback(
    async (data: SignInRequest) => {
      await signInMutation.mutateAsync(data);
    },
    [signInMutation],
  );

  const signUp = useCallback(
    async (data: SignUpRequest) => {
      await signUpMutation.mutateAsync(data);
    },
    [signUpMutation],
  );

  const confirmSignUp = useCallback(
    async (data: ConfirmSignUpRequest) => {
      await confirmSignUpMutation.mutateAsync(data);
    },
    [confirmSignUpMutation],
  );

  const resendVerificationCode = useCallback(
    async (data: ResendVerificationCodeRequest) => {
      await resendVerificationCodeMutation.mutateAsync(data);
    },
    [resendVerificationCodeMutation],
  );

  const forgotPassword = useCallback(
    async (data: ForgotPasswordRequest) => {
      await forgotPasswordMutation.mutateAsync(data);
    },
    [forgotPasswordMutation],
  );

  const resetPassword = useCallback(
    async (data: ResetPasswordRequest) => {
      await resetPasswordMutation.mutateAsync(data);
    },
    [resetPasswordMutation],
  );

  const refreshAuth = useCallback(async () => {
    await refreshMutation.mutateAsync();
  }, [refreshMutation]);

  const clearError = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []); // No dependencies needed - state setters are stable

  // OAuth mutations
  const oauthSignInMutation = useMutation({
    mutationFn: async ({
      provider,
      redirectTo: _redirectTo,
    }: {
      provider: OAuthProvider;
      redirectTo?: string;
    }) => {
      const response = await authApi.initiateOAuthSignIn({
        provider,
        redirectUri: `${window.location.origin}/auth/callback`,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      const currentPath =
        typeof window === "undefined"
          ? "/"
          : `${window.location.pathname}${window.location.search}`;
      const returnTo = sanitizeRedirectPath(currentPath);
      const redirectTo = sanitizeRedirectPath(variables?.redirectTo, returnTo);

      // Store state for CSRF validation with additional context
      const stateData = {
        state: data.state,
        provider: variables.provider,
        timestamp: Date.now(),
        origin: window.location.origin,
        redirectTo,
        returnTo,
      };

      window.sessionStorage.setItem("oauth_state_data", JSON.stringify(stateData));
      // Keep the old key for backward compatibility
      window.sessionStorage.setItem("oauth_state", data.state);

      logger.debug("OAuth initiated", {
        provider: variables.provider,
        state: data.state,
        stateData,
      });

      analyticsService.track("user.oauth_initiated", {
        provider: variables.provider.toLowerCase(),
      });

      // Redirect to the provider's Cognito Hosted UI flow
      window.location.href = data.authorizationUrl;
    },
    onError: (error: unknown, variables) => {
      const err = error as { message?: string };
      const providerLabel = variables.provider === "APPLE" ? "Apple" : "Google";
      setError(err?.message || `Failed to initiate ${providerLabel} sign-in`);
      setStatus("error");
    },
  });

  const handleOAuthCallbackMutation = useMutation({
    mutationFn: async ({ code, state }: { code: string; state: string }) => {
      logger.debug("OAuth callback received", { code, state });

      // Enhanced state validation with debugging
      const storedState = window.sessionStorage.getItem("oauth_state");
      const storedStateData = window.sessionStorage.getItem("oauth_state_data");

      logger.debug("OAuth state validation", {
        storedState,
        storedStateData,
      });

      let stateData: { provider?: OAuthProvider } | null = null;
      if (storedStateData) {
        try {
          stateData = JSON.parse(storedStateData);
          logger.debug("Parsed state data", { stateData });
        } catch (e) {
          logger.warn("Failed to parse stored state data", { error: e });
        }
      }

      // Validate state for CSRF protection
      if (state !== storedState) {
        logger.error("State mismatch", {
          received: state,
          stored: storedState,
          stateData,
        });
        throw new Error("Invalid OAuth state - possible CSRF attack");
      }

      logger.debug("State validation passed, proceeding with OAuth callback");

      const response = await authApi.handleOAuthCallback({
        code,
        state,
        redirectUri: `${window.location.origin}/auth/callback`,
      });
      const provider = stateData?.provider ?? "GOOGLE";
      return { ...response.data, provider };
    },
    onSuccess: data => {
      const {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        cognitoRefreshToken: newCognitoRefreshToken,
        provider,
        user,
      } = data;

      updateAccessToken(newAccessToken);
      updateRefreshToken(newRefreshToken || null);
      updateCognitoRefreshToken(newCognitoRefreshToken || null);
      setStatus("authenticated");
      setIsAuthenticated(true);
      setError(null);

      // Set user data immediately from OAuth response
      queryClient.setQueryData(queryKeys.auth.me, {
        success: true,
        data: user,
      });
      setOptimisticUser(user);
      setStoredUser(user);

      // Store email for refresh token usage
      setStoredEmail(user.email);

      // Identify user via email
      analyticsService.identify(user.email, {
        email: user.email,
        username: user.username,
      });
      analyticsService.track("user.signed_in", { method: provider.toLowerCase() });

      // Clean up OAuth state
      window.sessionStorage.removeItem("oauth_state");
      window.sessionStorage.removeItem("oauth_state_data");
      window.sessionStorage.removeItem(MIGRATION_RETRY_KEY);

      logger.debug("OAuth callback successful, user authenticated");
    },
    onError: (error: unknown) => {
      const err = error as { data?: { cause?: string; authCode?: string }; message?: string };
      logger.error("OAuth callback error", { error });

      // The server migrated a pre-federation account and issued no session for
      // this attempt. Starting authorization again resolves to the original
      // profile — once, then the marker below blocks a second.
      const alreadyRetried = window.sessionStorage.getItem(MIGRATION_RETRY_KEY) === "1";
      if (err?.data?.authCode === AUTH_MIGRATION_RETRY_REQUIRED && !alreadyRetried) {
        let retryProvider: OAuthProvider = "GOOGLE";
        try {
          const stored = window.sessionStorage.getItem("oauth_state_data");
          retryProvider = stored ? (JSON.parse(stored).provider ?? "GOOGLE") : "GOOGLE";
        } catch {
          // Unparseable stored state: fall back to the default provider.
        }
        window.sessionStorage.setItem(MIGRATION_RETRY_KEY, "1");
        window.sessionStorage.removeItem("oauth_state");
        window.sessionStorage.removeItem("oauth_state_data");
        logger.info("Account migrated to federation, restarting authorization once");
        oauthSignInMutation.mutate({ provider: retryProvider });
        return;
      }

      const errorMessage = err?.message || "OAuth authentication failed";
      setError(errorMessage);
      setStatus("error");

      // The retry guard goes with it: this attempt is over, so a later one gets
      // its own retry rather than inheriting a spent marker for the tab's life.
      window.sessionStorage.removeItem("oauth_state");
      window.sessionStorage.removeItem("oauth_state_data");
      window.sessionStorage.removeItem(MIGRATION_RETRY_KEY);
    },
  });

  // OAuth method implementations
  // A user-initiated sign-in is a new attempt, so it clears the retry guard —
  // the automatic retry calls the mutation directly and keeps the marker it set.
  const signInWithGoogle = useCallback(
    async (options?: { redirectTo?: string }) => {
      window.sessionStorage.removeItem(MIGRATION_RETRY_KEY);
      await oauthSignInMutation.mutateAsync({
        provider: "GOOGLE",
        redirectTo: options?.redirectTo,
      });
    },
    [oauthSignInMutation],
  );

  const signInWithApple = useCallback(async () => {
    window.sessionStorage.removeItem(MIGRATION_RETRY_KEY);
    await oauthSignInMutation.mutateAsync({ provider: "APPLE" });
  }, [oauthSignInMutation]);

  const handleOAuthCallback = useCallback(
    async (code: string, state: string) => {
      await handleOAuthCallbackMutation.mutateAsync({ code, state });
    },
    [handleOAuthCallbackMutation],
  );

  const contextValue: AuthContextType = useMemo(
    () => ({
      user: userData?.data || optimisticUser || null,
      accessToken,
      status,
      isLoading: status === "loading",
      isAuthenticated,
      hasInitialized,
      isBootstrapping,
      error,
      errorCode,
      signIn,
      signUp,
      confirmSignUp,
      resendVerificationCode,
      forgotPassword,
      resetPassword,
      signOut,
      refreshAuth,
      clearError,
      // OAuth methods
      signInWithGoogle,
      signInWithApple,
      handleOAuthCallback,
    }),
    [
      userData?.data,
      optimisticUser,
      accessToken,
      status,
      isAuthenticated,
      hasInitialized,
      isBootstrapping,
      error,
      errorCode,
      signIn,
      signUp,
      confirmSignUp,
      resendVerificationCode,
      forgotPassword,
      resetPassword,
      signOut,
      refreshAuth,
      clearError,
      signInWithGoogle,
      signInWithApple,
      handleOAuthCallback,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
